import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { PostMemberArchiveBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/**
 * Mutation hook for archiving a member. Cascade is unconditional server-side,
 * so we invalidate the per-patient read for each pet in the response - archive
 * only flips `archivedAt` on the patient row, so child-resource keys
 * (`patient-orders`, `patient-timeline`, ...) don't need invalidation.
 */
export function useArchiveMember(memberId: string) {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: PostMemberArchiveBody) => {
      const response = await api.post('/members/:memberId/archive', {
        params: { memberId },
        body,
      })
      if (!response.success) throw new Error(response.error.message)
      return response.data.member
    },
    onSuccess: (member) => {
      queryClient.invalidateQueries({ queryKey: ['member', memberId] })
      queryClient.invalidateQueries({ queryKey: ['members'] })
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      for (const pet of member.pets) {
        queryClient.invalidateQueries({ queryKey: ['patient', pet.id] })
      }
    },
  })
}
